import {
  FlashMessageService,
  ApiClient,
  CryptoLocalStorage,
  TokenService
} from "zoox-infra";
import i18n from "@/middlewares/i18n";
import queryString from "query-string";

export default class PersonService {
  constructor() {
    this.apiClient = new ApiClient();
  }

  /**
   * Returns the first person matching a given document type and number
   * @param  {[type]} documentTypeId Document type identifier
   * @param  {[type]} documentNumber Document number
   * @return {[type]}                [description]
   */
  getPersonByDocFirst(documentTypeId, documentNumber) {
    return this.apiClient.get(
      `open/person/searchbydocumentfirst-v2/${documentTypeId}/${documentNumber}`,
      false,
      true
    );
  }

  /**
   * Returns all people matching a given document type and document number
   * @param  {[type]} documentTypeId Document type identifier
   * @param  {[type]} documentNumber Document number
   * @return {[type]}                [description]
   */
  getPersonByDoc(documentTypeId, documentNumber) {
    return this.apiClient.get(
      `open/person/searchbydocument-v2/${documentTypeId}/${documentNumber}`,
      false,
      true
    );
  }

  async tempLogin(personId) {
    const resp = await this.apiClient.post("person/tempLogin", { personId });
    if (resp && resp.id && resp.token) {
      this.setLoginData(resp);
    }
    return resp;
  }

  async login(data) {
    let loginResponse;
    await this.apiClient
      .post("person/login", data, null, false, true)
      .then(resp => {
        if (resp && resp.id && resp.token) {
          this.setLoginData(resp);
        }
        loginResponse = resp;
      })
      .catch(err => {
        if (err && err.status == 401) {
          FlashMessageService.error(
            i18n.tc("INFRA.PORTAL_CUSTOM_ERRORS.LOGIN_401")
          );
        } else if (err) {
          err.showException = true;
          this.apiClient.handleError(err);
        }
        loginResponse = err;
      });
    return loginResponse;
  }

  async facialLogin(image) {
    const resp = await this.apiClient.post("person/facial-login", { image });
    if (resp && resp.id && resp.token) {
      this.setLoginData(resp);
    }
    return resp;
  }

  async register(data) {
    return this.apiClient.post("person/register", data);
  }

  async forgotPassword(data) {
    return this.apiClient.post("person/forgot-password", data);
  }

  async recoverPassword(data) {
    return this.apiClient.post("person/recover-password", data);
  }

  async confirmRegistration(data) {
    return this.apiClient.post("person/confirm-registration", data);
  }

  async resendRegistrationCode(data) {
    return this.apiClient.post("person/recover-confirmation-code", data);
  }

  async getContactsList(data) {
    return this.apiClient.post(`person/${data.id}/contacts`, data);
  }

  setLoginData(person) {
    this.setPersonId(person.id);
    this.setPersonName(person.name);
    TokenService.saveToken(person.token);
  }

  logout() {
    CryptoLocalStorage.removeItem("person_id");
    TokenService.destroyToken();
  }

  setPersonId(personId = null) {
    CryptoLocalStorage.setItem("person_id", personId);
  }

  getPersonId() {
    return CryptoLocalStorage.getItem("person_id") || null;
  }

  getPersonToken() {
    return TokenService.getToken();
  }

  setPersonName(personName = null) {
    CryptoLocalStorage.setItem("person_name", personName);
  }

  getPersonName() {
    return CryptoLocalStorage.getItem("person_name") || null;
  }

  isAuthenticated() {
    const token = this.getPersonToken();
    return token != null;
  }

  async getDocuments(id, documentTypeId) {
    const qs = queryString.stringify({ documentTypeId });
    return this.apiClient.get(`open/person/${id}/documents?${qs}`, false);
  }

  async saveDocs(personId, docs) {
    return this.apiClient.post(
      `open/person/${personId}/documents`,
      docs,
      null,
      false
    );
  }
}
